import React, { useCallback, useEffect, useState } from "react";

import logger from "../../../../../utils/LoggerFrontend.js";
import ChannelDataEditor from "../../Campaign__Create_ChannelDataEditor.jsx";
import MusicPlayer from "../../MusicPlayer/MusicPlayer.jsx";
import CampaignCreatePhotoEditor from "../../campaign-create-photo-editor/CampaignCreatePhotoEditor.jsx";
import { channelsByShortName } from "../../../../../data/app__channels.js";
import * as SC from "./PostEditorSpotify.styled.js";
import { useTranslation } from "react-i18next";
import AudioLibrary from "../../AudioLibrary/AudioLibrary.jsx";
import useComponentVisible from "../../../../../hooks/useComponentVisible.js";
import { TabsContextProvider } from "../../../../../context/Tabs.context.jsx";
import { createPortal } from "react-dom";
import { RightToLeftMotion } from "../../../../../styles/animations/right-to-left-motion";
import TemplatePostPreview from "../../TemplatePost/TemplatePost__Preview/TemplatePostPreview.jsx";
import useMediaAssets from "../hooks/useMediaAssets.js";

const PostEditorSpotify = ({
  orgaId,
  postData,
  channelData,
  updatePost,
  editable,
  channel,
  updateChannelData,
  selectedCreativeIndexToEdit,
  campaignLanguage
}) => {
  const { t } = useTranslation();

  const mediaAssets = useMediaAssets(postData);

  const templatePreviewSizes = {
    minHeight: SC.SPOTIFY_IMAGE_SIZE_SPEC.minHeight,
    maxHeight: SC.SPOTIFY_IMAGE_SIZE_SPEC.maxHeight,
    width: SC.SPOTIFY_IMAGE_SIZE_SPEC.pictureWidth,
    minAspect: channelsByShortName.spotify.minAspect,
    maxAspect: channelsByShortName.spotify.maxAspect
  };

  const [musicData, setMusicData] = useState({});

  const [portalContainer, setPortalContainer] = useState(null);

  const {
    ref: audioLibraryRef,
    isComponentVisible: showAudioLibrary,
    setIsComponentVisible: setShowAudioLibrary
  } = useComponentVisible(false, updateAudioData, "right-side-portal");

  const updateAudioData = useCallback(
    (updateObject) => {
      logger.log("PostEditorSpotify:MusicPlayer:updateAudioData", "called", { updateObject });
      updatePost({ audioData: Object.assign(updateObject) });
      setShowAudioLibrary(false);
    },
    [updatePost, setShowAudioLibrary]
  );

  const closeLibrary = useCallback(() => {
    setShowAudioLibrary(false);
  }, [setShowAudioLibrary]);

  useEffect(() => {
    if (postData?.audioData) {
      setMusicData(postData.audioData);
    }
  }, [postData]);

  logger.debug("PostEditorSpotify:render", "render:", {
    postData,
    channelData,
    updatePost,
    editable,
    musicData
  });

  useEffect(() => {
    const container = document.getElementById("right-side-portal");
    if (container) {
      setPortalContainer(container);
    }
  }, []);

  return (
    <SC.PostEditorSpotifyWrapper ref={audioLibraryRef}>
      <SC.MobileHead>9:41</SC.MobileHead>
      <SC.Header>
        <SC.Ad>{t("Ad", { lng: campaignLanguage })}</SC.Ad>
        <SC.ChannelDataHeaderWrapper>
          <ChannelDataEditor
            updateChannelData={updateChannelData}
            value={channelData.companyName}
            dataKey="companyName"
            textStyle={SC.companyNameStyle}
            limit={channelsByShortName.spotify.limits.companyName}
            label="Firmenname"
            tooltip="Firmenname"
            editable={editable}
            padding="3px 1px 3px 6px"
            dark
          />
          :
          <ChannelDataEditor
            updateChannelData={updateChannelData}
            value={channelData.title}
            dataKey="title"
            textStyle={SC.titleStyle}
            limit={channelsByShortName.spotify.limits.title}
            label={t("Job Title")}
            tooltip={t("Job Title")}
            editable={editable}
            padding="3px 6px 3px 3px"
            dark
          />
        </SC.ChannelDataHeaderWrapper>
      </SC.Header>
      <TemplatePostPreview
        orgaId={orgaId}
        creatives={postData.creatives}
        creativeAssets={postData.creativeAssets}
        selectedMediaAssetIndexToEdit={selectedCreativeIndexToEdit}
        mediaAssets={mediaAssets}
        updatePost={updatePost}
        channel={channel}
        editable={editable}
        templatePreviewSizes={templatePreviewSizes}
      />
      <SC.PhotoWrapper>
        <SC.PageLogoWrapper>
          <CampaignCreatePhotoEditor
            orgaId={orgaId}
            postDataKey="logos"
            creatives={postData?.logos || []}
            selectedCreativeIndexToEdit={selectedCreativeIndexToEdit}
            updatePost={updatePost}
            minHeight={50}
            maxHeight={50}
            minAspect={1}
            maxAspect={1}
            width={50}
            borderRadius="100%"
            editable={editable}
            isLogo={true}
          />
        </SC.PageLogoWrapper>
        <SC.ApplyButton target="_blank" href={channelData.url}>
          {t("Apply now", { lng: campaignLanguage })}
        </SC.ApplyButton>
      </SC.PhotoWrapper>
      <MusicPlayer
        postData={musicData}
        showAudioLibrary={showAudioLibrary}
        setShowAudioLibrary={setShowAudioLibrary}
      />
      {portalContainer &&
        createPortal(
          <TabsContextProvider defaultTab="Generierung">
            <RightToLeftMotion show={showAudioLibrary}>
              <AudioLibrary
                id="audio-library-view"
                orgaId={postData?.orgaId}
                campaignId={postData?.campaignId}
                audioData={musicData}
                updateAudioData={updateAudioData}
                closeLibrary={closeLibrary}
                channel={channelData.channel || "spotify"}
                channelData={channelData}
                isAutoGenerated={postData?.isAutoGenerated}
                autoGeneratedText={postData?.autoGeneratedText}
                isAutoGenerationStreaming={postData?.isAutoGenerationStreaming}
              />
            </RightToLeftMotion>
          </TabsContextProvider>,

          portalContainer
        )}
    </SC.PostEditorSpotifyWrapper>
  );
};

export default PostEditorSpotify;
